import clsx from 'clsx';
import { FC, useState } from 'react';
import { ShowError } from '../../../errors/components/ShowError';
import {
	useBindTelegramGroup,
	useUnbindTelegramGroup,
} from '../api/telegram.mutations';
import {
	useGetTelegramBind,
	useGetTelegramTagTopics,
} from '../api/telegram.queries';
import {
	TelegramBindStatus,
	TelegramTagTopic,
	TelegramWorkerStatus,
} from '../model';

const formatDateTime = (iso?: string | null): string => {
	if (!iso) return '—';
	return new Date(iso).toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const PUBLICATION_STATUS_LABEL: Record<string, string> = {
	pending: 'Публикуется',
	sent: 'Опубликовано',
	failed: 'Ошибка',
};

const PUBLICATION_ACTION_LABEL: Record<string, string> = {
	publish: 'публикация',
	edit: 'обновление',
	delete: 'удаление',
};

/** Здоровье очереди публикаций: её видно только здесь. */
const WorkerCard: FC<{ worker: TelegramWorkerStatus | null }> = ({ worker }) => {
	if (!worker) return null;

	return (
		<div className='card mb-5'>
			<div className='card-header'>
				<h3 className='card-title'>Очередь публикаций</h3>
			</div>
			<div className='card-body'>
				{!worker.alive && (
					<div className='alert alert-danger'>
						Очередь никто не разбирает — фоновая задача не отвечает. Новости
						не будут опубликованы, пока это не починят. Обратитесь к бэкенду.
					</div>
				)}

				<div className='row g-5'>
					<div className='col-6 col-md-3'>
						<div className='text-muted fs-7'>Состояние</div>
						<div
							className={clsx(
								'fw-bold fs-5',
								worker.alive ? 'text-success' : 'text-danger'
							)}
						>
							{worker.alive ? 'Работает' : 'Не отвечает'}
						</div>
					</div>
					<div className='col-6 col-md-3'>
						<div className='text-muted fs-7'>Последний прогон</div>
						<div className='fw-bold fs-6'>{formatDateTime(worker.last_tick)}</div>
					</div>
					<div className='col-6 col-md-3'>
						<div className='text-muted fs-7'>В очереди</div>
						<div className='fw-bold fs-5'>{worker.pending}</div>
					</div>
					<div className='col-6 col-md-3'>
						<div className='text-muted fs-7'>Провалено</div>
						<div
							className={clsx(
								'fw-bold fs-5',
								worker.failed > 0 ? 'text-danger' : ''
							)}
						>
							{worker.failed}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

/** Экран «группа ещё не привязана»: инструкция и ввод кода. */
const BindForm: FC = () => {
	const [code, setCode] = useState('');
	const [error, setError] = useState<any>();

	const { mutate: bind, isLoading } = useBindTelegramGroup();

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setError(undefined);
		bind(
			{ code: code.trim().toUpperCase() },
			{
				onSuccess: () => setCode(''),
				onError: err => setError(err),
			}
		);
	};

	return (
		<div className='card'>
			<div className='card-header'>
				<h3 className='card-title'>Привязка Telegram-группы</h3>
			</div>
			<div className='card-body'>
				<div className='alert alert-primary'>
					<div className='fw-bold mb-2'>Как привязать группу</div>
					<ol className='mb-0 ps-4'>
						<li>Добавьте бота в супергруппу.</li>
						<li>
							Включите в группе темы (топики) и сделайте бота администратором
							с правом <b>«Управление темами»</b>.
						</li>
						<li>
							Отправьте в группу команду <code>/bind</code> и введите
							полученный код ниже. Код действует 15 минут.
						</li>
					</ol>
					<div className='mt-3 mb-0'>
						Группа уходит под новости целиком: темы в ней создаются сами — по
						одной на тег, при первой публикации новости с этим тегом. Создавать
						их руками не нужно.
					</div>
				</div>

				<form onSubmit={handleSubmit} noValidate>
					<div className='row g-5'>
						<div className='col-12 col-md-4'>
							<label className='required fw-bold fs-6 mb-2'>Код привязки</label>
							<input
								type='text'
								className='form-control form-control-solid text-uppercase'
								placeholder='K7M2QP'
								value={code}
								maxLength={16}
								autoComplete='off'
								onChange={e => setCode(e.target.value)}
							/>
						</div>
					</div>

					<div className='mt-7'>
						<button
							type='submit'
							className='btn btn-primary'
							disabled={isLoading || code.trim().length < 4}
						>
							{isLoading ? (
								<span>
									Привязываем...{' '}
									<span className='spinner-border spinner-border-sm align-middle ms-1' />
								</span>
							) : (
								'Привязать группу'
							)}
						</button>
					</div>

					{error && <ShowError error={error} />}
				</form>
			</div>
		</div>
	);
};

/** Экран «группа привязана»: данные и отвязка. */
const BoundCard: FC<{ status: TelegramBindStatus }> = ({ status }) => {
	const [confirmUnbind, setConfirmUnbind] = useState(false);
	const [error, setError] = useState<any>();

	const { mutate: unbind, isLoading: isUnbinding } = useUnbindTelegramGroup();

	const handleUnbind = () => {
		setError(undefined);
		unbind(undefined, {
			onSuccess: () => setConfirmUnbind(false),
			onError: err => setError(err),
		});
	};

	const publication = status.last_publication;

	return (
		<div className='card'>
			<div className='card-header'>
				<h3 className='card-title'>Telegram-группа</h3>
				<div className='card-toolbar'>
					<span className='badge badge-light-success fs-7'>Привязана</span>
				</div>
			</div>
			<div className='card-body'>
				<div className='row g-5 mb-8'>
					<div className='col-12 col-md-4'>
						<div className='text-muted fs-7'>Группа</div>
						<div className='fw-bold fs-5'>{status.chat_title || '—'}</div>
						<div className='text-muted fs-7'>{status.chat_id}</div>
					</div>
					<div className='col-12 col-md-4'>
						<div className='text-muted fs-7'>Темы</div>
						<div className='fw-bold fs-5'>По тегам новостей</div>
						<div className='text-muted fs-7'>
							создаются при первой публикации
						</div>
					</div>
					<div className='col-12 col-md-4'>
						<div className='text-muted fs-7'>Привязана</div>
						<div className='fw-bold fs-6'>{formatDateTime(status.updated)}</div>
					</div>
				</div>

				{publication && (
					<div className='mb-8'>
						<div className='text-muted fs-7 mb-1'>Последняя публикация</div>
						<div className='d-flex align-items-center gap-3 flex-wrap'>
							<span
								className={clsx('badge fs-7', {
									'badge-light-success': publication.status === 'sent',
									'badge-light-warning': publication.status === 'pending',
									'badge-light-danger': publication.status === 'failed',
								})}
							>
								{PUBLICATION_STATUS_LABEL[publication.status] || publication.status}
							</span>
							<span className='text-muted fs-7'>
								{PUBLICATION_ACTION_LABEL[publication.action] || publication.action},{' '}
								{formatDateTime(publication.updated)}
							</span>
						</div>
						{publication.last_error && (
							<div className='text-danger fs-7 mt-2'>{publication.last_error}</div>
						)}
					</div>
				)}

				<div className='separator mb-8' />

				{!confirmUnbind ? (
					<button
						type='button'
						className='btn btn-light-danger'
						onClick={() => setConfirmUnbind(true)}
					>
						Отвязать группу
					</button>
				) : (
					<div className='alert alert-warning'>
						<div className='mb-3'>
							После отвязки новости публиковаться не будут. Темы тегов и уже
							опубликованные сообщения останутся в Telegram, но привязка тем
							сбросится: после повторной привязки они создадутся заново. Чтобы
							привязать группу заново, отправьте в неё <code>/bind</code>.
						</div>
						<div className='d-flex gap-3'>
							<button
								type='button'
								className='btn btn-danger btn-sm'
								onClick={handleUnbind}
								disabled={isUnbinding}
							>
								{isUnbinding ? 'Отвязываем...' : 'Да, отвязать'}
							</button>
							<button
								type='button'
								className='btn btn-light btn-sm'
								onClick={() => setConfirmUnbind(false)}
								disabled={isUnbinding}
							>
								Отмена
							</button>
						</div>
					</div>
				)}

				{error && <ShowError error={error} />}
			</div>
		</div>
	);
};

const tagLabel = (tagName: Record<string, string>): string =>
	tagName?.ru || tagName?.en || Object.values(tagName || {})[0] || '—';

/**
 * Темы тегов: только чтение. Создать тему руками нельзя — она появляется с
 * первой публикацией, поэтому пустой список это норма, а не поломка.
 */
const TagTopicsCard: FC<{ topics: TelegramTagTopic[]; isLoading: boolean }> = ({
	topics,
	isLoading,
}) => (
	<div className='card mt-5'>
		<div className='card-header'>
			<h3 className='card-title'>Темы по тегам</h3>
			<div className='card-toolbar'>
				<span className='badge badge-light fs-7'>{topics.length}</span>
			</div>
		</div>
		<div className='card-body'>
			{isLoading ? (
				<div className='text-muted'>Загрузка...</div>
			) : topics.length === 0 ? (
				<div className='text-muted'>
					Тем пока нет. Тема создаётся сама при первой публикации новости с
					соответствующим тегом.
				</div>
			) : (
				<div className='table-responsive'>
					<table className='table table-row-bordered align-middle'>
						<thead>
							<tr className='fw-bold text-muted'>
								<th>Тег</th>
								<th>Тема в Telegram</th>
								<th>thread id</th>
								<th>Создана</th>
							</tr>
						</thead>
						<tbody>
							{topics.map(topic => (
								<tr key={topic.tag_uuid}>
									<td className='fw-bold'>{tagLabel(topic.tag_name)}</td>
									<td>{topic.name}</td>
									<td className='text-muted'>{topic.message_thread_id}</td>
									<td className='text-muted'>{formatDateTime(topic.created)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			<div className='form-text mt-3'>
				Тема закрыта для участников — писать в неё может только бот.
				Переименование тега переименовывает и тему, удаление тега тему
				закрывает.
			</div>
		</div>
	</div>
);

export const TelegramForumPage: FC = () => {
	const { data: status, isLoading } = useGetTelegramBind();
	const isBound = Boolean(status?.bound);
	const { data: tagTopics, isLoading: isTopicsLoading } =
		useGetTelegramTagTopics(isBound);

	if (isLoading) {
		return (
			<div className='d-flex justify-content-center py-20'>
				<div className='spinner-border text-primary' role='status' />
			</div>
		);
	}

	return (
		<>
			<WorkerCard worker={status?.worker ?? null} />
			{status?.bound ? <BoundCard status={status} /> : <BindForm />}
			{isBound && (
				<TagTopicsCard
					topics={tagTopics?.topics ?? []}
					isLoading={isTopicsLoading}
				/>
			)}
		</>
	);
};
